import { useT } from '@/session/useSession'
import { FilterChips } from '../FilterChips/FilterChips'
import { Button } from '../ui/button'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { chipsOf } from './chipsOf'
import { DebouncedInput } from './DebouncedInput'
import styles from './FilterBar.module.css'

export type FilterChoice = { value: string; label: string }

export type FilterSelectSpec = {
  name: string
  label: string
  value: string
  options: FilterChoice[]
}

export type FilterTextSpec = {
  name: string
  label: string
  value: string
  placeholder: string
}

export type FilterBarProps = {
  search: string
  searchPlaceholder: string
  selects: FilterSelectSpec[]
  texts?: FilterTextSpec[]
  onChange: (patch: Record<string, string | undefined>) => void
  onReset: () => void
}

export function FilterBar({
  search,
  searchPlaceholder,
  selects,
  texts = [],
  onChange,
  onReset,
}: FilterBarProps) {
  const t = useT()
  const send = (name: string, value: string) =>
    onChange({ [name]: value === '' ? undefined : value })

  return (
    <>
      <div className={styles.bar}>
        <DebouncedInput
          id="filter-search"
          label={t('filters.search')}
          value={search}
          placeholder={searchPlaceholder}
          type="search"
          onCommit={(value) => send('search', value)}
        />
        {texts.map((text) => (
          <DebouncedInput
            key={text.name}
            id={`filter-${text.name}`}
            label={text.label}
            value={text.value}
            placeholder={text.placeholder}
            onCommit={(value) => send(text.name, value)}
          />
        ))}
        {selects.map((select) => (
          <div className={styles.field} key={select.name}>
            <label className={styles.label} htmlFor={`filter-${select.name}`}>
              {select.label}
            </label>

            <Select value={select.value} onValueChange={(next) => send(select.name, String(next))}>
              <SelectTrigger id={`filter-${select.name}`}>
                <SelectValue placeholder={select.label}>
                  {(value) =>
                    select.options.find((option) => option.value === value)?.label ?? select.label
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent className={styles.options}>
                <SelectGroup>
                  <SelectItem value="">{select.label}</SelectItem>
                  {select.options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        ))}
        <Button variant="ghost" size="sm" className={styles.reset} onClick={onReset}>
          {t('filters.reset')}
        </Button>
      </div>

      <FilterChips
        chips={chipsOf(search, t('filters.search'), selects, texts)}
        onRemove={(name) => onChange({ [name]: undefined })}
      />
    </>
  )
}
